import type { ReactElement, ReactNode } from 'react';

import type { AgentProgressEvent } from '~/components/AiChat/AgentTimeline/types';
import type { ChatController, ChatExchangeView } from '~/components/AiChat/controller/types';
import type { UpdateExchangeUserFeedbackCallback } from '~/components/AiChat/types';

/**
 * @deprecated Use the new component composition pattern with MessageLayout + AiMessageContent/HumanMessageContent
 */
export interface BaseMessageProps {
  text: string;
  avatar: ReactElement;
  sender: string | ReactElement;
  isLoading: boolean;
  exchangeId?: number;
  onUserFeedback?: UpdateExchangeUserFeedbackCallback;
  onRegenerate?: () => void;
  withActions?: boolean;
  withFeedback?: boolean;
  withTypeEffect?: boolean;
  withDocuments?: boolean;
  withBorder?: boolean;
  onType?: () => void;
  onTypeEnd?: () => void;
  currentUserFeedback?: number;
  messageContainer: React.FC<{ className: string }>;
  referencesIds?: string[];
  showDisclaimer?: boolean;
  createdAt?: Date;
  chatDocumentIds?: number[];
  agentEvents?: AgentProgressEvent[];
  isGenerating?: boolean;
  responseTime?: number | null;
  isHuman?: boolean;
}

export interface MessageProps {
  exchange: ChatExchangeView;
}

export const AI_NAME = 'Clive';

export const MESSAGE_VARIANT = {
  AI: 'ai',
  HUMAN: 'human',
} as const;

export type MessageVariant = (typeof MESSAGE_VARIANT)[keyof typeof MESSAGE_VARIANT];

export type OnUserFeedbackCallback = (exchangeId: string, feedbackScore: number, feedbackText?: string) => void;

/**
 * Props for the shared MessageLayout component.
 * Handles outer container, avatar positioning, snackbar, and SafeComponent wrapper.
 */
export interface MessageLayoutProps {
  avatar: ReactElement;
  variant: MessageVariant;
  children: ReactNode;
  footer?: ReactNode;
}

/**
 * Props for AI-specific message content.
 */
export interface AiMessageContentProps {
  className?: string;
  text: string;
  sender?: string | ReactElement | null;
  isLoading: boolean;
  exchangeId?: string;
  onUserFeedback?: ChatController['sendFeedback'];
  onRegenerate?: () => void;
  withFeedback?: boolean;
  currentUserFeedback?: number;
  referencesIds?: string[];
  showDisclaimer?: boolean;
  createdAt?: string;
  agentEvents?: AgentProgressEvent[];
  isGenerating?: boolean;
  responseTime?: number | null;
  onCopy: () => void;
  // Async agentic chat: exchange failed to generate; renders an error + retry instead of the answer.
  isFailed?: boolean;
  onRetry?: () => void;
}

/**
 * Props for human-specific message content.
 */
export interface HumanMessageContentProps {
  text: string;
  sender?: string | ReactElement;
  chatDocumentIds?: number[];
  withDocuments?: boolean;
  withBorder?: boolean;
}
